import React, { useCallback, useEffect } from 'react';
import CloudArrowUpIcon from '@atlaskit/icon/core/cloud-arrow-up';
import { defineMessage, FormattedMessage } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import ButtonMenuItemWithAnalytics from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/button-menu-item-with-analytics/index.tsx';
import { getProjectMenuItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/utils/getProjectMenuItemAnalytics.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ProjectCreateDrawerConsumer } from '@jira/project-create__drawer-context/src/index.tsx';
import { useRovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
import { useFirstActivationDateMs } from '@atlassian/jira-tenant-context-controller/src/components/first-activation-date-ms/index.tsx';
import { isWithinLast } from '@jira/platform__onboarding-core/src/utils/is-within-last/index.tsx';

const importWork = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.menu-item.label',
	defaultMessage: 'Import work',
	description: 'Label for the Import menu item in the project sidebar footer',
});

/**
 * ImportWorkMenuItem — shown in the sidenav footer for new admins in their first 7 days.
 *
 * Clicking opens the X2C import wizard drawer directly.
 *
 * Eligibility (in order, matching Statsig/Switcheroo exposure-scoping best practice —
 * expVal must stay last so exposure is only logged for the truly eligible population):
 *  1. canCreateProject + the `jfind-retest-left-nav-import-killswitch` feature gate
 *     (checked by the parent Footer component, which controls whether this component
 *     even mounts).
 *  2. isRovoLLMEnabled — site must be Rovo-enabled.
 *  3. isSiteWithin7Days — site must be less than 7 days old.
 *  4. expVal('jfind-retest-left-nav-import', 'isEnabled', false) — experiment bucketing.
 *
 * Phase 2: ImportChoiceModal (wizard + Rovo upload options) will be added in a separate PR.
 */
export const ImportWorkMenuItem = (): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// Hooks must run unconditionally (Rules of Hooks) — the killswitch already gated
	// whether this component mounts at all, via the parent Footer.
	const { data: rovoEntitlement } = useRovoEntitlementResource();
	const activationDateMs = useFirstActivationDateMs();

	const isRovoLLMEnabled = rovoEntitlement?.isRovoLLMEnabled ?? false;
	const isSiteWithin7Days =
		typeof activationDateMs === 'number' ? isWithinLast({ days: 7 })(activationDateMs) : false;

	// expVal is a plain function call (not a hook), so it's safe — and required for
	// correct exposure scoping — to keep it short-circuited behind the other checks.
	const isInExperiment =
		isRovoLLMEnabled &&
		isSiteWithin7Days &&
		expVal('jfind-retest-left-nav-import', 'isEnabled', false);

	const importWorkAnalytics = getProjectMenuItemAnalytics({
		itemId: 'importWork',
	});

	useEffect(() => {
		if (!isInExperiment) {
			return;
		}
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'importWorkMenuItem',
			}),
		);
	}, [isInExperiment, createAnalyticsEvent]);

	const handleClick = useCallback(
		(open: (args: { isImportProjectOpenInitially: boolean }) => void) => () => {
			open({ isImportProjectOpenInitially: true });
		},
		[],
	);

	if (!isInExperiment) {
		return null;
	}

	return (
		<ProjectCreateDrawerConsumer>
			{({ methods: { open } }) => (
				<ButtonMenuItemWithAnalytics
					onClick={handleClick(open)}
					elemBefore={<CloudArrowUpIcon label="" color="currentColor" />}
					analytics={importWorkAnalytics}
					data-testid="sidebar-nav4-sidebars-content-projects-import-work.menu-item"
				>
					<FormattedMessage {...importWork} />
				</ButtonMenuItemWithAnalytics>
			)}
		</ProjectCreateDrawerConsumer>
	);
};
